import { Box, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useTranslation } from "react-i18next";
import { TalentStatus } from "../../../../features/talents/types/common";
import { leadingZerosDateFormat } from "../../../helpers/formatDateTime";
import { FlexVerticalAligned } from "../../styled/styled";
import { getTalentStatusIcon } from "./TalentStatusIcons";

export enum StatusTextStyle {
  SHORT = "SHORT",
  LONG = "LONG",
  TOOLTIP = "TOOLTIP",
}

type Props = {
  status?: TalentStatus;
  statusChanged?: string;
  statusTextStyle?: StatusTextStyle;
  statusTextOverride?: string;
};

type TalentStatusData = {
  icon: React.ReactNode;
  text: string;
};
/**
 * @param statusTextStyle - enum defines how the status text is displayed
 * - SHORT: Show status text, e.g. "Valmis työhön"
 * - LONG: Show full text, e.g. "Valmis työhön (hyväksytty 12.03.2024)"
 * - TOOLTIP: Show only icon with tooltip containing full text
 */
const TalentStatusIcon = ({
  status,
  statusChanged,
  statusTextStyle = StatusTextStyle.TOOLTIP,
  statusTextOverride,
}: Props) => {
  const { t } = useTranslation("talentProfile");

  if (!status) return null;

  const formattedStatusChanged = statusChanged
    ? format(new Date(statusChanged), leadingZerosDateFormat)
    : null;

  const getStatusText = (status: TalentStatus) => {
    switch (statusTextStyle) {
      case StatusTextStyle.SHORT:
        return t(`${status}_long`);
      case StatusTextStyle.LONG:
        return formattedStatusChanged
          ? t(`${status}_statusText`, { date: formattedStatusChanged })
          : t(`${status}_long`);
      case StatusTextStyle.TOOLTIP:
        return formattedStatusChanged
          ? t(`${status}_statusText`, { date: formattedStatusChanged })
          : t(status);
      default:
        return StatusTextStyle.SHORT;
    }
  };

  const talentStatusData: Record<TalentStatus, TalentStatusData> = {
    [TalentStatus.ACTIVE]: {
      icon: getTalentStatusIcon(TalentStatus.ACTIVE),
      text: getStatusText(TalentStatus.ACTIVE),
    },
    [TalentStatus.INCOMPLETE]: {
      icon: getTalentStatusIcon(TalentStatus.INCOMPLETE),
      text: getStatusText(TalentStatus.INCOMPLETE),
    },
    [TalentStatus.NEW]: {
      icon: getTalentStatusIcon(TalentStatus.NEW),
      text: getStatusText(TalentStatus.NEW),
    },
    [TalentStatus.INTERVIEW_PENDING]: {
      icon: getTalentStatusIcon(TalentStatus.INTERVIEW_PENDING),
      text: getStatusText(TalentStatus.INTERVIEW_PENDING),
    },
    [TalentStatus.INTERVIEW]: {
      icon: getTalentStatusIcon(TalentStatus.INTERVIEW),
      text: getStatusText(TalentStatus.INTERVIEW),
    },
    [TalentStatus.LATER]: {
      icon: getTalentStatusIcon(TalentStatus.LATER),
      text: getStatusText(TalentStatus.LATER),
    },
    [TalentStatus.EMPLOYED_ELSEWHERE]: {
      icon: getTalentStatusIcon(TalentStatus.EMPLOYED_ELSEWHERE),
      text: getStatusText(TalentStatus.EMPLOYED_ELSEWHERE),
    },
    [TalentStatus.NOT_EMPLOYED]: {
      icon: getTalentStatusIcon(TalentStatus.NOT_EMPLOYED),
      text: getStatusText(TalentStatus.NOT_EMPLOYED),
    },
    [TalentStatus.DISABLED]: {
      icon: getTalentStatusIcon(TalentStatus.DISABLED),
      text: getStatusText(TalentStatus.DISABLED),
    },
    [TalentStatus.DEACTIVATED]: {
      icon: getTalentStatusIcon(TalentStatus.DEACTIVATED),
      text: getStatusText(TalentStatus.DEACTIVATED),
    },
  };

  return (
    <Box id="talent-status-icon">
      {statusTextStyle === StatusTextStyle.TOOLTIP ? (
        <Tooltip title={talentStatusData[status].text}>
          <Box display="flex" alignItems="center" ml="4px">
            {talentStatusData[status].icon}
          </Box>
        </Tooltip>
      ) : (
        <FlexVerticalAligned ml="4px" gap="6px">
          <Box display="flex" alignItems="center" mr="4px">
            {talentStatusData[status].icon}
          </Box>
          <Typography lineHeight="24px">
            {statusTextOverride ?? talentStatusData[status].text}
          </Typography>
        </FlexVerticalAligned>
      )}
    </Box>
  );
};

export default TalentStatusIcon;
